import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  bootstrapCurrentUser,
  bootstrapCurrentPermission,
  bootstrapSettingsDetails,
} from '@/store/bootstrap/actions';
import { fetchLogin, login, logout } from '@/store/auth/actions';
import { fetchAllUserPreferences } from '@/store/ServerStorage';

import { selectIsLoggingIn, selectIsLoggedIn } from '@/store/auth/selectors';
import {
  selectBootstrapLoading,
  selectBootstrapError,
  selectCurrUser,
  selectSettings,
} from '@/store/bootstrap/selectors';
import { clearLocalStorage } from '@/utils/storageKeys';
import { startBootRequests } from '@/store/bootstrap/earlyRequests';

/** Where the access token lives. Exported so other tabs can watch for it. */
export const AUTH_STORAGE_KEY = 'apiv1Auth';

export const useAuth = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isBootstraping = useSelector(selectBootstrapLoading);
  const bootstrapError = useSelector(selectBootstrapError);
  const currUser = useSelector(selectCurrUser);
  const settingsDetails = useSelector(selectSettings);

  const isLoggingIn = useSelector(selectIsLoggingIn);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const getAuth = () => {
    return localStorage.getItem(AUTH_STORAGE_KEY);
  };

  const setAuth = (accessToken) => {
    localStorage.setItem(AUTH_STORAGE_KEY, accessToken);
  };

  const deleteAuth = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const doFetchLogin = async () => {
    try {
      return await dispatch(fetchLogin()).unwrap();
    } catch (error) {
      console.error('Error fetching login', error);
    }
  };

  const doLogin = async ({ email, password, otp = '' }) => {
    try {
      const result = await dispatch(login({ email, password, otp })).unwrap();
      const accessToken = result?.data?.access_token;
      if (accessToken) setAuth(accessToken);
      return result;
    } catch (error) {
      console.error('Error logging in', error);
    }
  };
  const doLogout = async () => {
    try {
      await dispatch(logout());
      clearLocalStorage();
      history.push('/login');
    } catch (error) {
      console.error('Error logging out', error);
    }
  };

  const bootstrap = async () => {
    try {
      // The shell waits on all four; they used to start only once `/me` had
      // answered (~0.3s on staging). Start them together — each is still applied
      // only where it was before, so a suspended or signed-out account discards
      // the permission and preference answers exactly as it skipped them.
      const early = startBootRequests();
      const currUserRes = await dispatch(bootstrapCurrentUser()).unwrap();
      dispatch(bootstrapSettingsDetails({ prefetched: early.settings }));
      if (currUserRes?.data?.account?.status === 'suspended') {
        return currUserRes;
      }
      if (!currUserRes?.data?.id) {
        return currUserRes;
      }
      const [currPermissionRes] = await Promise.all([
        dispatch(
          bootstrapCurrentPermission({ prefetched: early.permission })
        ).unwrap(),
        fetchAllUserPreferences(early.preferences),
      ]);
      return [currUserRes, currPermissionRes];
    } catch (error) {
      console.error('Error bootstrapping', error);
    }
  };

  return {
    getAuth,
    setAuth,
    deleteAuth,
    fetchLogin: doFetchLogin,
    login: doLogin,
    logout: doLogout,
    bootstrap,

    isBootstraping,
    bootstrapError,
    currUser,
    settingsDetails,

    isLoggingIn,
    isLoggedIn,
  };
};
